import { useState } from 'react';
import type { KostenuebernahmeForm, ErklaerungApiError } from '@/types/erklaerung';
import { submitErklaerung } from '@/api/erklaerung';

interface Props {
  token: string;
  form: KostenuebernahmeForm;
  onBack: () => void;
  onDone: () => void;
}

function labelAnteil(pct: 50 | 100) {
  return pct === 100 ? '100 %' : '50 %';
}
function labelZahlungsart(v: string) {
  return v === 'einmalig' ? 'Einmalig (Gesamtstudiengebühr)' : 'Monatlich (Monatsrate)';
}
function labelZahlungsweise(v: string) {
  return v === 'ueberweisung' ? 'Überweisung' : 'SEPA-Lastschrift';
}

export function ErklaerungStep3SignAndSubmit({ token, form, onBack, onDone }: Props) {
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const canSubmit = check1 && check2 && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setValidationErrors([]);
    try {
      await submitErklaerung(token, form);
      onDone();
    } catch (err: unknown) {
      const apiErr = err as ErklaerungApiError;
      if (apiErr.code === 'already_signed') {
        onDone();
        return;
      }
      if (apiErr.code === 'validation') {
        setValidationErrors(apiErr.errors ?? ['Ungültige Eingabe.']);
      } else {
        setValidationErrors(['Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.']);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Checkboxes */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <h2 className="text-sm font-semibold text-slate-700">Bestätigung & Signatur</h2>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={check1}
              onChange={e => setCheck1(e.target.checked)}
              className="mt-0.5 accent-teal-600"
            />
            <span className="text-sm text-slate-700">
              Ich bin berechtigt, für{' '}
              <span className="font-semibold">{form.rechnung_firma}</span>{' '}
              diese Erklärung abzugeben.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={check2}
              onChange={e => setCheck2(e.target.checked)}
              className="mt-0.5 accent-teal-600"
            />
            <span className="text-sm text-slate-700">
              Hiermit signiere ich die Erklärung digital. Mir ist bekannt, dass die Erklärung dieselbe
              rechtliche Bindung hat wie eine handschriftliche Unterschrift.
            </span>
          </label>

          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          <div className="pt-2 flex flex-col gap-3">
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg px-4 py-2.5 text-sm transition-colors"
            >
              {loading ? 'Wird eingereicht…' : 'Erklärung signieren und einreichen'}
            </button>
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="w-full text-slate-600 hover:text-slate-800 text-sm py-1.5 disabled:opacity-50"
            >
              ← Zurück zur Bearbeitung
            </button>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Vorschau Ihrer Angaben</h2>
          <dl className="space-y-2 text-sm">
            <PreviewRow label="Anteil" value={labelAnteil(form.anteil_prozent)} />
            <PreviewRow label="Zahlungsart" value={labelZahlungsart(form.zahlungsart)} />
            <PreviewRow label="Zahlungsweise" value={labelZahlungsweise(form.zahlungsweise)} />
            <div className="border-t border-slate-200 pt-2 mt-2" />
            <PreviewRow label="Firma" value={form.rechnung_firma} />
            {form.rechnung_adresszusatz && (
              <PreviewRow label="Adresszusatz" value={form.rechnung_adresszusatz} />
            )}
            {form.rechnung_abteilung && (
              <PreviewRow label="Abteilung" value={form.rechnung_abteilung} />
            )}
            <PreviewRow label="Ansprechpartner" value={form.rechnung_ap_name} />
            <PreviewRow label="E-Mail AP" value={form.rechnung_email_ap} />
            {form.rechnung_email_invoice && (
              <PreviewRow label="E-Mail Rechnung" value={form.rechnung_email_invoice} />
            )}
            {form.rechnung_telefon && (
              <PreviewRow label="Telefon" value={form.rechnung_telefon} />
            )}
            <PreviewRow
              label="Adresse"
              value={`${form.rechnung_strasse} ${form.rechnung_hausnummer}, ${form.rechnung_plz} ${form.rechnung_ort}`}
            />
          </dl>
        </div>
      </div>
    </form>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-slate-500 min-w-32 shrink-0">{label}:</dt>
      <dd className="text-slate-800 font-medium">{value}</dd>
    </div>
  );
}
